import { FC, useEffect, useState } from 'react';
import FilterRow from './components/FilterRow';
import HighlightCards from './components/HighlightCards';
import TotalEmploymentGrowth from './components/TotalEmploymentGrowth';
import TopJobRoles from './components/TopJobRoles';
import TopSectors from './components/TopSectors';
import SkillClusterMap from './components/SkillClusterMap';

import { useFilter } from './contexts/FilterContext';

type EmploymentGrowthPeriod = {
  name: string;
  value: number;
};

type Occupation = {
  value: string;
  code: string;
  sectorCode: string;
  EmploymentGrowth: {
    [key: string]: EmploymentGrowthPeriod[]; // e.g., "3 Months", "6 Months", etc.
  };
  mostInDemandRole: {
    [key: string]: string;
  };
};
type SkillItem = {
  skill: string;
  category: string;
  growthRate: string;
  relativeDemand: number;
  jobRoles: string[];
  sectors?: string[];
  occupations?: string[];
};

type Props = {
  onViewAllJobs: () => void;
  onViewAllSkills: () => void;
};

const LabourForecastDashboard: FC<Props> = ({ onViewAllJobs, onViewAllSkills }) => {
  const { selectedSector, selectedOccupation, timeRange, selectedTab, setSelectedTab } = useFilter();
  const [sectors, setSectors] = useState([]);
  const [occupations, setOccupations] = useState<Occupation[]>([]);
  const [skillsData, setSkillsData] = useState<SkillItem[]>([]);
  const [topEmergingSkill, setTopEmergingSkill] = useState<string>("");

  useEffect(() => {
    const loadJson = async () => {
      try {
        const [sectorsData, occupationsData, skillsData] = await Promise.all([
          import(`./json/${selectedTab}/sectors.json`),
          import(`./json/${selectedTab}/occupations.json`),
          import(`./json/${selectedTab}/skillsData.json`)
        ]);
        setSectors(sectorsData.default);
        setOccupations(occupationsData.default);
        setSkillsData(skillsData.default);

        const topSkill = skillsData.default
          .filter((skill: SkillItem) => {
            const sectorMatch = selectedSector && selectedSector !== 'All'
              ? skill.sectors?.includes(selectedSector)
              : true;
const occupationMatch =
  !selectedOccupation || selectedOccupation === "All"
    ? true
    : skill.occupations?.includes(selectedOccupation);
            return sectorMatch && occupationMatch;
          })
          .map((skill: SkillItem) => ({

            ...skill,
            growthRate: parseFloat(skill.growthRate),
          }))
          .filter((skill: { growthRate: number }) => !isNaN(skill.growthRate))
          .sort((a: { growthRate: number }, b: { growthRate: number }) => b.growthRate - a.growthRate)[0]
        if (topSkill) {
        setTopEmergingSkill(topSkill.skill);
      } else {
        setTopEmergingSkill(''); // fallback to empty string
        console.warn("No top skill found for current filters.");
      }

      } catch (err) {
        console.error('Error loading data for', selectedTab, err);
        setSectors([]);
        setOccupations([]);
      }
    };
    loadJson();
  }, [selectedTab, selectedSector, selectedOccupation]);


  const mostGrowingOccupation = occupations
    .filter((occ) =>
      !selectedSector || selectedSector === 'All' || occ.sectorCode === selectedSector
    )
    .map((occ) => {
      const growthData = occ.EmploymentGrowth?.[timeRange as keyof typeof occ.EmploymentGrowth];
      if (!growthData || growthData.length < 2) return null;

      const initial = growthData[0].value;
      const latest = growthData[growthData.length - 1].value;
      const growthRate = (latest - initial) / initial;

      return {
        name: occ.value,
        growthRate,
      };
    })
    .filter((o): o is { name: string; growthRate: number } => o !== null)
    .sort((a, b) => b.growthRate - a.growthRate)[0];


  const mostInDemandRole = occupations
    .filter((occ) => {
      const sectorMatch = selectedSector && selectedSector !== 'All' ? occ.sectorCode === selectedSector : true;
      const occupationMatch = selectedOccupation ? occ.code === selectedOccupation : true;
      return sectorMatch && occupationMatch;
    })
    .map((occ) => {
      const roleName = occ.mostInDemandRole?.[timeRange as keyof typeof occ.mostInDemandRole];
      return roleName || null;
    })
    .filter((role): role is string => !!role && role !== 'All')[0];


  const highlightCards = [
    {
      icon: <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
        <path d="M25.8666 14.6666H40.8M40.8 14.6666V29.6M40.8 14.6666L25.8666 29.6L18.4 22.1333L7.19995 33.3333" stroke="#043C5B" strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>,
      title: mostGrowingOccupation?.name || 'N/A',
      subtitle: 'Fastest Growing Occupation',
    },
    {
      icon: <svg xmlns="http://www.w3.org/2000/svg" width="49" height="48" viewBox="0 0 49 48" fill="none">
        <path d="M41.1667 28.2998V36.7999C41.1667 38.9889 39.5929 40.8718 37.423 41.1599C33.2496 41.714 28.9915 42 24.6667 42C20.3418 42 16.0838 41.714 11.9104 41.1599C9.74048 40.8718 8.16669 38.9888 8.16669 36.7999V28.2998M41.1667 28.2998C42.1105 27.4953 42.6667 26.2778 42.6667 24.9778V17.4114C42.6667 15.2495 41.1309 13.3816 38.9928 13.0617C36.742 12.7249 34.4657 12.4663 32.1667 12.2886M41.1667 28.2998C40.7795 28.6299 40.327 28.8905 39.8209 29.0588C35.0573 30.6425 29.9622 31.5 24.6667 31.5C19.3713 31.5 14.2761 30.6425 9.51246 29.0587C9.00637 28.8905 8.5539 28.6299 8.16669 28.2998M8.16669 28.2998C7.22284 27.4952 6.66669 26.2777 6.66669 24.9777V17.4114C6.66669 15.2495 8.20249 13.3816 10.3406 13.0617C12.5913 12.7249 14.8677 12.4663 17.1667 12.2886M32.1667 12.2886V10.5C32.1667 8.01472 30.152 6 27.6667 6H21.6667C19.1814 6 17.1667 8.01472 17.1667 10.5V12.2886M32.1667 12.2886C29.6918 12.0974 27.1906 12 24.6667 12C22.1428 12 19.6416 12.0974 17.1667 12.2886M24.6667 25.5H24.6817V25.515H24.6667V25.5Z" stroke="#043C5B" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>,
      title: mostInDemandRole || 'N/A',
      subtitle: 'Most In Demand Role',
    },
    {
      icon: <svg xmlns="http://www.w3.org/2000/svg" width="49" height="48" viewBox="0 0 49 48" fill="none">
        <path d="M6.33325 9H34.8333M6.33325 18H25.8333M6.33325 27H16.8333M27.3333 25.5L34.8333 18M34.8333 18L42.3333 25.5M34.8333 18V42" stroke="#043C5B" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>,
      title: topEmergingSkill || 'N/A',
      subtitle: 'Top Emerging Skill',
    },
  ];

  return (
    <div className="labourForecast-dashboard px-4 py-3">
      <div className="container-fluid pb-3 mb-3 px-0 d-flex justify-content-between align-items-center rounded" style={{ borderBottom: '1px solid #E9EDF2' }}>
        <div>
          <h2 className="mb-1 text-dark fw-semibold">Future of Work Forecast Dashboard</h2>
          <p className="mb-0 text-secondary">Understand emerging job roles, key skills, and future workforce needs.</p>
        </div>
        <div className="d-flex gap-2">
          {['PulseCast', 'Aggressive', 'Conservative', 'Realistic'].map((label) => (
            <button
              key={label}
              type="button"
              className={`btn border rounded-pill ${selectedTab === label ? 'bg-primary text-white' : 'bg-white text-dark'}`}
              style={{
                borderColor: '#D7DAE0',
                padding: '6px 16px',
                fontSize: 14,
                fontWeight: 500
              }}
              onClick={() => setSelectedTab(label)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="container-fluid pb-3 px-0">
        <FilterRow sectors={sectors} occupations={occupations} showTimeRange={true} />
      </div>
      <div className="container-fluid pb-3 px-0">
        <HighlightCards cards={highlightCards} />
      </div>
      <div className="container-fluid px-0">
        <div className="d-flex flex-wrap gap-4 pb-3">
          <TotalEmploymentGrowth />
          <TopJobRoles onViewAllJobs={onViewAllJobs} />
        </div>
        <div className="d-flex flex-wrap gap-4 pb-3">
          <TopSectors />
          <SkillClusterMap onViewAllSkills={onViewAllSkills} />
        </div>
      </div>
    </div>
  );
};

export default LabourForecastDashboard;
